/* eslint-disable no-await-in-loop  */
import { Action, Dispatch } from 'redux';
import { IAppState } from '../reducers';
import {
  ActionType as GameScheduleAction,
  updateFormType, FormType, selectUploadSport,
} from './gameScheduleForm';
import toast, { superAdminToast } from '../../utils/toast';
import { Sport, VideoCodec } from '../../enums';
import { selectIsUserVaultSuperAdmin } from '../user/userOrgRole';
import { VaultUploadStatus } from '../vaultUploadDialog';
import {
  canUploadAdditionalAttachments, isFootball, isHockey, isLacrosse,
} from '../../utils/exchange';
import { deleteAssets } from '../../api/vault/assets';
import { queryJobs } from '../jobStatus';
import { getVideoTypeFromProduct } from '../product';
import { additionalAttachments } from '../../constants/exchange';

// Forced qualifier for hockey xml uploads - the picker is locked when it's applied
export const XML_QUALIFIER = 'XML';

export interface IExchangeFile {
  filename: string;
  mediaFile: File;
  exchangeFile?: File;
  uploadStatus: VaultUploadStatus;
  qualifier?: string;
  progressPercentage?: string;
  assetId?: string;
  videoAngle?: string;
  videoCodec?: VideoCodec;
  other?: File[];
  comment?: string;
}

export interface IState {
  isOpen: boolean,
  isError: boolean,
  selectedFiles: IExchangeFile[],
  isUploading: boolean;
  isUploadComplete: boolean;
}

export enum ActionType {
  Toggle = 'UploadExchange.Toggle',
  ToggleError = 'UploadExchange.ToggleError',
  UpdateSchedules = 'UploadExchange.UpdateSchedules',
  UpdateFiles = 'UploadExchange.UpdateFiles',
  ClearAll = 'UploadExchange.ClearAll',
  SetIsUploading = 'SetIsUploading',
  UpdateCanceled = 'UploadExchange.UpdateCanceled',
}

export interface IUploadExchangeAction extends Action<ActionType> {
  payload?: any;
}

const INITIAL_STATE: IState = {
  isOpen: false,
  isError: false,
  isUploading: false,
  isUploadComplete: false,
  selectedFiles: [],
};

export default function reducer(state: IState = INITIAL_STATE, action: IUploadExchangeAction): IState {
  switch (action.type) {
    case ActionType.Toggle:
      if (!state.isOpen) window.EXCHANGE_UPLOAD_CANCELED = false;
      return {
        ...state,
        isOpen: !state.isOpen,
      };
    case ActionType.UpdateFiles:
      return {
        ...state,
        selectedFiles: action.payload,
      };
    case ActionType.ClearAll:
      return {
        ...INITIAL_STATE,
      };
    case ActionType.SetIsUploading:
      return {
        ...state,
        isUploading: action.payload,
      };
    case ActionType.UpdateCanceled:
      return {
        ...INITIAL_STATE,
        selectedFiles: [],
      };
    default:
      return state;
  }
}

export const toggleUploadExchangeDialog = () => (dispatch: Dispatch, getState: () => IAppState) => {
  const {
    exchange: {
      createGameScheduleDialog: {
        isOpen,
      },
    },
  } = getState();
  if (!isOpen) dispatch(updateFormType(FormType.Upload));
  dispatch({ type: ActionType.Toggle });
};

const FOURCC_CODEC_MAP: Record<string, VideoCodec> = {
  avc1: VideoCodec.H264,
  avc3: VideoCodec.H264,
  hev1: VideoCodec.H265,
  hvc1: VideoCodec.H265,
  av01: VideoCodec.AV1,
  vp09: VideoCodec.VP9,
  mp4v: VideoCodec.MPEG4,
  apch: VideoCodec.ProRes, // ProRes 422 High Quality
  apcn: VideoCodec.ProRes, // ProRes 422 Standard
  apcs: VideoCodec.ProRes, // ProRes 422 LT
  apco: VideoCodec.ProRes, // ProRes 422 Proxy
  ap4h: VideoCodec.ProRes, // ProRes 4444
  ap4x: VideoCodec.ProRes, // ProRes 4444 XQ
};

const readBoxHeader = async (file: File, offset: number): Promise<{ size: number; type: string }> => {
  const headerBuf = await file.slice(offset, offset + 8).arrayBuffer();
  const view = new DataView(headerBuf);
  let size = view.getUint32(0);
  const type = String.fromCharCode(view.getUint8(4), view.getUint8(5), view.getUint8(6), view.getUint8(7));
  if (size === 1) {
    // 64-bit extended size
    const extBuf = await file.slice(offset + 8, offset + 16).arrayBuffer();
    const extView = new DataView(extBuf);
    size = extView.getUint32(4); // lower 32 bits (sufficient for files < 4GB)
  }
  return { size, type };
};

const getVideoCodec = async (file: File): Promise<VideoCodec> => {
  try {
    // Walk top-level MP4 boxes to find 'moov' (may be at end of file after 'mdat')
    let offset = 0;
    while (offset < file.size) {
      const { size, type } = await readBoxHeader(file, offset); // eslint-disable-line no-await-in-loop
      if (size === 0) break;
      if (type === 'moov') {
        const moovSize = Math.min(size, 256 * 1024);
        const moovBuf = await file.slice(offset, offset + moovSize).arrayBuffer();
        const bytes = new Uint8Array(moovBuf);
        for (let i = 0; i < bytes.length - 3; i += 1) {
          const fourCC = String.fromCharCode(bytes[i], bytes[i + 1], bytes[i + 2], bytes[i + 3]);
          if (FOURCC_CODEC_MAP[fourCC]) return FOURCC_CODEC_MAP[fourCC];
        }
        return VideoCodec.Unknown;
      }
      offset += size;
    }
  } catch { /** Ignore */ }
  return VideoCodec.Unknown;
};

const getExchangeQualifier = (fileName: string, sport: string): string => {
  if (isFootball(sport)) {
    if (fileName.includes(' d ') || fileName.includes(' def ')) {
      return 'D';
    } if (fileName.includes(' o ') || fileName.includes(' off ')) {
      return 'O';
    } if (fileName.includes(' k ') || fileName.includes(' kick ') || fileName.includes(' kicks ') || fileName.includes(' st ') || fileName.includes(' spt ')) {
      return 'K';
    } if (fileName.includes(' program ') || fileName.includes(' fg ') || fileName.includes(' game ')) {
      return 'Full Sequence';
    }
  } else if (isLacrosse(sport)) { // Default qualifier for lacrosse is 'Full Game' since there aren't any other qualifiers defined
    return 'Full Game';
  }
  return null;
};

export const updateFiles = (files: Array<File>) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const { exchange: { uploadDialog: { selectedFiles } } } = state;
  // Rules come from the game being uploaded to, not the user's own product sport
  const sport = selectUploadSport(state);

  // Create map with filename as key and an object with media, exchange, and zip files as values
  const filenameMap = new Map<string, {
    media: File;
    exchange: File;
    zip: File;
    other: File[];
  }>();
  const getFileMapValue = (filename: string) => {
    if (!filenameMap.has(filename)) {
      filenameMap.set(filename, {
        media: null, exchange: null, zip: null, other: [],
      });
    }
    return filenameMap.get(filename);
  };

  // Add values to map
  for (let i = 0; i < files.length; i += 1) {
    const file = files[i];
    const filename = file.name.split('.').slice(0, -1).join('.');
    const fileType = file.name.split('.').pop().toLowerCase();
    let duplicate = false;
    if (fileType === 'mp4' || fileType === 'mov') {
      if (getFileMapValue(filename).media) duplicate = true;
      getFileMapValue(filename).media = file;
    } else if (fileType === 'xchange') {
      if (getFileMapValue(filename).exchange) duplicate = true;
      getFileMapValue(filename).exchange = file;
    } else if (fileType === 'xml' && isHockey(sport)) {
      // Hockey xml uploads as its own asset; key by full name so it never pairs with a same-named media file
      if (getFileMapValue(file.name).media) duplicate = true;
      getFileMapValue(file.name).media = file;
    } else if (fileType === 'zip' && sport === Sport.Softball) {
      if (getFileMapValue(filename).zip) duplicate = true;
      getFileMapValue(filename).zip = file;
    } else if (canUploadAdditionalAttachments(sport) && additionalAttachments(sport).includes(fileType)) {
      getFileMapValue(filename).other.push(file);
    } else {
      superAdminToast(false, `Unsupported File Type: ${fileType}`, { type: toast.TYPE.ERROR });
      return;
    }

    if (duplicate) {
      superAdminToast(false, `Duplicate file: ${filename}`, { type: toast.TYPE.ERROR });
      return;
    }
  }

  // Verify that the files are correctly paired and not missing
  const fileEntries = Array.from(filenameMap.entries());
  const exchangeFiles: IExchangeFile[] = [];
  for (let i = 0; i < fileEntries.length; i += 1) {
    const [filename, {
      media, exchange, zip, other,
    }] = fileEntries[i];
    if (!media && !zip) {
      superAdminToast(false, `Missing media file: ${filename}`, { type: toast.TYPE.ERROR });
      return;
    }
    if (exchange && !media) {
      superAdminToast(false, `Missing media file with exchange file: ${filename}`, { type: toast.TYPE.ERROR });
      return;
    }
    if (sport === Sport.Football && !exchange) {
      superAdminToast(false, 'Football uploads require both media and xchange files', { type: toast.TYPE.ERROR });
      return;
    }
    const mediaOrZip = media || zip;
    const isXmlAsset = isHockey(sport) && mediaOrZip.name.toLowerCase().endsWith('.xml');
    // eslint-disable-next-line no-await-in-loop
    const videoCodec = media && !isXmlAsset ? await getVideoCodec(media) : VideoCodec.Unknown;
    exchangeFiles.push({
      filename,
      mediaFile: mediaOrZip,
      exchangeFile: exchange || null,
      uploadStatus: VaultUploadStatus.NotStarted,
      qualifier: isXmlAsset ? XML_QUALIFIER : getExchangeQualifier(filename, sport),
      videoAngle: null,
      videoCodec,
      other,
    });
  }

  // Check that the files are not already selected
  const selectedFileNames = selectedFiles.map((file) => file.filename);
  if (exchangeFiles.some((file) => selectedFileNames.includes(file.filename))) {
    superAdminToast(false, 'Selected files already exist in the list', { type: toast.TYPE.ERROR });
    return;
  }

  dispatch({
    type: ActionType.UpdateFiles,
    payload: [...selectedFiles, ...exchangeFiles],
  });
};

export const clearAll = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const { product: { target }, exchange: { uploadDialog: { selectedFiles } } } = state;
  const isUploadComplete = selectIsUploadsCompleted(state);
  window.EXCHANGE_UPLOAD_CANCELED = true;
  dispatch({
    type: ActionType.ClearAll,
  });
  dispatch({
    type: GameScheduleAction.ClearAll,
  });

  const assetIds = selectedFiles.filter((file) => file.assetId).map((file) => file.assetId);
  if (assetIds && assetIds.length > 0 && !isUploadComplete) {
    try {
      console.log('Deleting assets: ', assetIds);
      await deleteAssets({ ids: assetIds });
    } catch (error) { /** Ignore */ }
    const isVaultSuperAdmin = selectIsUserVaultSuperAdmin(state);
    queryJobs(getVideoTypeFromProduct(target), true, isVaultSuperAdmin)(dispatch, getState);
  }
};

export const setFileUploadStatus = (index: number, uploadStatus: VaultUploadStatus) => async (dispatch: Dispatch<any>, getState: () => IAppState) => {
  const files = selectSelectedFiles(getState());
  if (files && files.length > 0) {
    if (!files[index]) return;
    files[index].uploadStatus = uploadStatus;
    files[index].progressPercentage = null;
    dispatch({
      type: ActionType.UpdateFiles,
      payload: [...files],
    });
  }
};

// This updates the progress for the file during upload (progress is normally calculated per stage (e.g. pre-processing, uploading etc)
export const setFileUploadProgress = (index: number, progressPercentage: string) => async (dispatch: Dispatch<any>, getState: () => IAppState) => {
  const files = selectSelectedFiles(getState());
  if (files && files.length > 0) {
    if (!files[index]) return;
    files[index].progressPercentage = progressPercentage;
    dispatch({
      type: ActionType.UpdateFiles,
      payload: [...files],
    });
  }
};

export const setFileUploadQualifier = (index: number, qualifier: string) => async (dispatch: Dispatch<any>, getState: () => IAppState) => {
  const files = selectSelectedFiles(getState());
  if (files && files.length > 0) {
    if (!files[index]) return;
    files[index].qualifier = qualifier;
    dispatch({
      type: ActionType.UpdateFiles,
      payload: [...files],
    });
  }
};

// VV-226: per-file comment, carried onto the asset as exchangeComment at upload.
//
// Replaces the file object rather than mutating it in place. The sibling setters above assign
// straight into the object held in state, so the old and new arrays share it and any previous
// snapshot or memoised consumer sees the change retroactively. Same flaw, not fixed here to
// keep this change to the field it adds.
export const setFileUploadComment = (index: number, comment: string) => async (dispatch: Dispatch<any>, getState: () => IAppState) => {
  const files = selectSelectedFiles(getState());
  if (!files || !files[index]) return;
  dispatch({
    type: ActionType.UpdateFiles,
    payload: files.map((file, i) => (i === index ? { ...file, comment } : file)),
  });
};

export const setFileUploadVideoAngle = (index: number, videoAngle: string) => async (dispatch: Dispatch<any>, getState: () => IAppState) => {
  const files = selectSelectedFiles(getState());
  if (files && files.length > 0) {
    if (!files[index]) return;
    files[index].videoAngle = videoAngle;
    dispatch({
      type: ActionType.UpdateFiles,
      payload: [...files],
    });
  }
};

export const setFileUploadAssetId = (index: number, assetId: string) => async (dispatch: Dispatch<any>, getState: () => IAppState) => {
  const files = selectSelectedFiles(getState());
  if (files && files.length > 0) {
    if (!files[index]) return;
    files[index].assetId = assetId;
    dispatch({
      type: ActionType.UpdateFiles,
      payload: [...files],
    });
  }
};

export const setIsUploading = (isUploading: boolean) => async (dispatch: Dispatch<any>, getState: () => IAppState) => {
  dispatch({
    type: ActionType.SetIsUploading,
    payload: isUploading,
  });
};

export const selectSelectedFiles = (state: IAppState): IExchangeFile[] => state.exchange.uploadDialog.selectedFiles;

export const selectIsUploading = (state: IAppState): boolean => state.exchange.uploadDialog.isUploading;

export const selectIsUploadsCompleted = (state: IAppState): boolean => selectSelectedFiles(state).length > 0 && selectSelectedFiles(state).every((file) => file.uploadStatus === VaultUploadStatus.Complete || file.uploadStatus === VaultUploadStatus.Error);
